import Accordion from "@/components/Accordion";
import { LanguageConfigModel } from "@/pages/Messaging/types";
import { Toggle } from "@velaro/velaro-component-lib";
import React, { useState } from "react";
import TextInput from "../../TextInput";
import { ChatWindow } from "../../types";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
}

type ActionType = "url" | "webhook" | "workflow";

const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  url: "Open URL",
  webhook: "Fire Webhook",
  workflow: "Trigger Workflow"
};

const divider = <div className="my-3 border-t border-slate-200" />;

const fieldLabel = (text: string, hint?: string) => (
  <div className="mb-1">
    <div className="text-sm font-medium">{text}</div>
    {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
  </div>
);

export function CustomAction(props: Props) {
  const lc = props.workingChatWindow.languageConfig || {};
  const api = useApi();
  const toastAlert = useToastAlert();

  const [tokenInput, setTokenInput] = useState("");
  const [savingToken, setSavingToken] = useState(false);

  const update = (updates: Partial<LanguageConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      languageConfig: { ...lc, ...updates }
    });
  };

  const actionType: ActionType = lc.customActionType ?? "url";

  const saveToken = async () => {
    if (!tokenInput.trim()) return;
    setSavingToken(true);
    try {
      const res = await api.messaging.put(
        `ChatWindow/${props.workingChatWindow.id}/custom-action-token`,
        { token: tokenInput }
      );
      if (!res.ok) throw new Error(await res.text());
      toastAlert.displayToast(Severity.Success, "Auth token saved.");
      setTokenInput("");
      update({ customActionWebhookHasToken: true });
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save token.");
    } finally {
      setSavingToken(false);
    }
  };

  const clearToken = async () => {
    try {
      const res = await api.messaging.put(
        `ChatWindow/${props.workingChatWindow.id}/custom-action-token`,
        { token: "" }
      );
      if (!res.ok) throw new Error(await res.text());
      update({ customActionWebhookHasToken: false });
      toastAlert.displayToast(Severity.Success, "Auth token removed.");
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to remove token.");
    }
  };

  return (
    <Accordion.Item eventKey="custom-action" onExpanded={props.onExpanded}>
      <Accordion.Header>
        <Accordion.Title>Custom Action</Accordion.Title>
        <Accordion.SubTitle>
          A configurable card that fires a webhook, opens a URL, or triggers a
          workflow when a visitor clicks the button.
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="mb-2 space-y-4">

          {/* Enable */}
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium">Enable</div>
            <Toggle
              toggled={lc.customActionEnabled ?? false}
              changeToggle={() => update({ customActionEnabled: !lc.customActionEnabled })}
            />
          </div>
          {divider}

          {/* Card content */}
          <div>
            {fieldLabel("Title", "Headline shown on the card")}
            <TextInput
              value={lc.customActionTitle ?? ""}
              setValue={(v) => update({ customActionTitle: v })}
              showEmojiButton={false}
            />
          </div>
          <div>
            {fieldLabel("Description", "Short supporting text below the title")}
            <TextInput
              value={lc.customActionBody ?? ""}
              setValue={(v) => update({ customActionBody: v })}
              showEmojiButton={false}
            />
          </div>
          <div>
            {fieldLabel("Button Text")}
            <TextInput
              value={lc.customActionButtonText ?? ""}
              setValue={(v) => update({ customActionButtonText: v })}
              showEmojiButton={false}
            />
          </div>
          {divider}

          {/* Action type selector */}
          <div>
            {fieldLabel("Action Type", "What happens when the visitor clicks the button")}
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-hover"
            value={actionType}
            onChange={(e) => update({ customActionType: e.target.value as ActionType })}
          >
            {(Object.entries(ACTION_TYPE_LABELS) as [ActionType, string][]).map(
              ([val, label]) => (
                <option key={val} value={val}>{label}</option>
              )
            )}
          </select>
          </div>

          {/* ── URL ────────────────────────────────── */}
          {actionType === "url" && (
            <>
              {divider}
              <div className="bg-slate-50 rounded-lg p-3 space-y-2 border border-slate-200">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                  URL Settings
                </div>
                {fieldLabel("Target URL")}
                <TextInput
                  value={lc.customActionTargetUrl ?? ""}
                  setValue={(v) => update({ customActionTargetUrl: v })}
                  showEmojiButton={false}
                />
                <div className="flex justify-between items-center pt-1">
                  <div className="text-sm font-medium">Open in new tab</div>
                  <Toggle
                    toggled={lc.customActionOpenInNewTab ?? true}
                    changeToggle={() =>
                      update({ customActionOpenInNewTab: !lc.customActionOpenInNewTab })
                    }
                  />
                </div>
              </div>
            </>
          )}

          {/* ── WEBHOOK ────────────────────────────── */}
          {actionType === "webhook" && (
            <>
              {divider}
              <div className="bg-slate-50 rounded-lg p-3 space-y-2 border border-slate-200">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                  Webhook Settings
                </div>

                {fieldLabel("Endpoint URL", "The URL that will receive the request")}
                <TextInput
                  value={lc.customActionWebhookUrl ?? ""}
                  setValue={(v) => update({ customActionWebhookUrl: v })}
                  showEmojiButton={false}
                />

                <div className="mt-1" />
                {fieldLabel("HTTP Method")}
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-hover"
                  value={lc.customActionWebhookMethod ?? "POST"}
                  onChange={(e) =>
                    update({ customActionWebhookMethod: e.target.value as "POST" | "GET" })
                  }
                >
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                </select>

                <div className="mt-1" />
                {fieldLabel(
                  "Payload Template",
                  "JSON body sent with the request. Use {{visitorId}} and {{siteId}} as placeholders."
                )}
                <textarea
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-hover resize-y"
                  rows={4}
                  placeholder={'{\n  "visitorId": "{{visitorId}}",\n  "action": "password_reset"\n}'}
                  value={lc.customActionWebhookPayloadTemplate ?? ""}
                  onChange={(e) =>
                    update({ customActionWebhookPayloadTemplate: e.target.value })
                  }
                />

                <div className="mt-1" />
                {fieldLabel(
                  "Authorization Token",
                  lc.customActionWebhookHasToken
                    ? "A token is currently saved. Enter a new value to replace it."
                    : "Bearer token sent in the Authorization header. Stored encrypted — never exposed to visitors."
                )}
                {lc.customActionWebhookHasToken && (
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs text-emerald-600 font-medium">
                      ✓ Token configured
                    </span>
                    <button
                      className="text-xs text-red-500 hover:underline"
                      onClick={clearToken}
                    >
                      Remove
                    </button>
                  </div>
                )}
                <div className="flex space-x-2">
                  <input
                    type="password"
                    className="flex-1 border rounded-lg px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-hover"
                    placeholder="Bearer token (write-only)"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button
                    disabled={!tokenInput.trim() || savingToken}
                    onClick={saveToken}
                    className="px-3 py-2 text-sm rounded-lg bg-primary text-white font-medium disabled:opacity-40 hover:bg-primary transition"
                  >
                    {savingToken ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── WORKFLOW ───────────────────────────── */}
          {actionType === "workflow" && (
            <>
              {divider}
              <div className="bg-slate-50 rounded-lg p-3 space-y-2 border border-slate-200">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                  Workflow Settings
                </div>
                {fieldLabel(
                  "Workflow ID",
                  "The ID of the Velaro workflow to trigger. Find it in the Workflows list URL."
                )}
                <input
                  type="number"
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-hover"
                  placeholder="e.g. 42"
                  value={lc.customActionWorkflowId ?? ""}
                  onChange={(e) =>
                    update({
                      customActionWorkflowId: e.target.value
                        ? Number(e.target.value)
                        : undefined
                    })
                  }
                  min={1}
                />
                <div className="text-xs text-slate-400">
                  The workflow is triggered server-side with the visitor's session context.
                  Use a Trigger node in your workflow to handle the action.
                </div>
              </div>
            </>
          )}

          {divider}

          {/* Feedback messages */}
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
            Visitor Feedback
          </div>
          <div>
            {fieldLabel("Success Message", "Shown to visitor after the action succeeds")}
            <TextInput
              value={lc.customActionSuccessMessage ?? ""}
              setValue={(v) => update({ customActionSuccessMessage: v })}
              showEmojiButton={false}
            />
          </div>
          <div>
            {fieldLabel("Error Message", "Shown if the action fails")}
            <TextInput
              value={lc.customActionErrorMessage ?? ""}
              setValue={(v) => update({ customActionErrorMessage: v })}
              showEmojiButton={false}
            />
          </div>
          {divider}

          {/* Open chat after */}
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm font-medium">Open chat after action</div>
              <div className="text-xs text-slate-400">
                Automatically open the chat window after the action runs
              </div>
            </div>
            <Toggle
              toggled={lc.customActionOpenChatAfter ?? false}
              changeToggle={() =>
                update({ customActionOpenChatAfter: !lc.customActionOpenChatAfter })
              }
            />
          </div>

        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
